import * as React from 'react'
import { useEffect } from 'react'
import type { JSX } from 'react'
import { ACTIONS, EVENTS, Joyride, STATUS } from 'react-joyride'
import type { EventData, Step } from 'react-joyride'
import { useMatch, useNavigate } from 'react-router-dom'

import { useLocalStorage } from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'
import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'
import { usePostComposer } from '~publish/hooks/usePostComposer'
import {
  channel as channelRoute,
  newCalendarSingleChannel,
} from '~publish/legacy/routes'
import { useSelectedChannel } from '~publish/pages/Channel/ChannelContext'
import { useCommunityConfig } from '~publish/pages/Community/CommunityConfig'
import { isTeamPlan } from '~publish/pages/Settings/Billing/utils/planUtils'

import { StepCoachmark } from './StepCoachmark'

export enum TourStep {
  SLOT = 0,
  MORE_MENU = 1,
}
const THIRTY_MINUTES_IN_MS = 30 * 60 * 1000
const CUSTOM_ACTIONS = {
  OPEN_COMPOSER: 'OpenComposer',
  NAVIGATE_TO_TEAM_SETTINGS: 'NAVIGATE_TO_TEAM_SETTINGS',
} as const

export const POSTING_GOAL_SLOTS_TOUR_STORAGE_KEY =
  'preference:posting-goal-slots-tour'
export enum TourStorageStep {
  CLOSED = 'closed', // finished the tour
  STEP_0 = '0',
  STEP_1 = '1',
}

function isTourStorageStep(value: string): value is TourStorageStep {
  return value === TourStorageStep.CLOSED || value === '0' || value === '1'
}

/**
 * Shared steps that appear in all tour variants
 * These steps are shown after the initial posting time/schedule step
 */
const sharedTourSteps: Step[] = [
  {
    target: '[data-tour-id="selected-channel-item"]',
    title: '🔐 Set Posting Permissions',
    content:
      'Choose which team members can create, schedule, and send posts for this channel. You can update these permissions anytime from Account > Team.',
    placement: 'right',
    offset: 0,
    data: {
      dismissableStep: true,
      actions: [
        {
          text: 'Skip',
          action: 'SKIP',
        },
        {
          text: 'Set Permissions',
          action: 'NAVIGATE_TO_TEAM_SETTINGS',
        },
      ],
    },
  },
  {
    target: '[data-tour-id="nav-item-community"]',
    title: 'Manage your replies with ease 💬',
    content:
      'Visit the Community tab to quickly reply and engage with your audience.',
    placement: 'bottom',
    offset: 0,
    data: {
      dismissableStep: false,
      actions: [
        {
          text: 'Got It',
        },
      ],
    },
  },
]

/**
 * Steps for the tour when the channel has a posting goal
 * @returns Step[]
 */
export const postingGoalSteps: Step[] = [
  {
    target: '[data-tour-id="slot"]',
    title: 'Use our recommended posting times 🎯',
    content:
      "Based on our research, we've suggested these times to maximize engagement. You can post at other times too!",
    skipBeacon: true,
    offset: 0,
    data: {
      dismissableStep: true,
      actions: [
        {
          text: 'Next',
        },
      ],
    },
  },
  {
    target: '[data-tour-id="channel-settings"]',
    title: 'Adjust your schedule ✏️',
    content: 'Edit your posting goal, posting times, and other settings here.',
    offset: 0,
    data: {
      dismissableStep: false,
      actions: [
        {
          text: 'Got It',
        },
      ],
    },
  },
  ...sharedTourSteps,
]

/**
 * Steps for the tour when the channel does not have a posting goal
 * @returns Step[]
 */
export const postingScheduleSteps: Step[] = [
  {
    target: '[data-tour-id="slot"]',
    title: "Don't have a goal in mind? No worries! ⏰",
    content:
      "We've recommended some posting times based on our research, so you can schedule posts in one click.",
    skipBeacon: true,
    offset: 0,
    data: {
      dismissableStep: true,
      actions: [
        {
          text: '+ New Post',
          action: CUSTOM_ACTIONS.OPEN_COMPOSER,
        },
      ],
    },
  },
  ...sharedTourSteps,
]

/**
 * Checks if any dialog is currently open in the document by looking for elements with
 * role="dialog" and data-state="open"
 * @returns {boolean} True if any dialog is open, false otherwise
 */
function isAnyDialogOpen(): boolean {
  const dialogs = document.querySelectorAll(
    '[role="dialog"][data-state="open"]',
  )
  return dialogs.length > 0
}

/**
 * React hook that tracks whether any dialog is open in the document
 * Uses a MutationObserver to watch for changes to dialog states
 *
 * [HACK]: This is needed because due to how the Composer is implemented, we
 * need to render floating elements (popover, dialogs, etc) in the
 * "portals-container" and those floating elements are not properly placed on
 * their corresponding part of the DOM with their corresponding z-index. Once we
 * move the composer to use Popcorn this should be fixed and this kind of hacks
 * will not be needed anymore.
 * @returns {boolean} True if any dialog is open, false otherwise
 */
function useIsDialogOpen(): boolean {
  const [isDialogOpen, setIsDialogOpen] = React.useState(isAnyDialogOpen())

  useEffect(
    function trackDialogStateChanges() {
      const checkDialogs = (): void => {
        setIsDialogOpen(isAnyDialogOpen())
      }

      const observer = new MutationObserver(checkDialogs)
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-state'],
      })

      return (): void => observer.disconnect()
    },
    [setIsDialogOpen],
  )

  return isDialogOpen
}

export const PostingGoalSlotsTour = (): JSX.Element => {
  const [slotsTourCurrentStep, setSlotsTourCurrentStep] = useLocalStorage(
    POSTING_GOAL_SLOTS_TOUR_STORAGE_KEY,
    TourStorageStep.STEP_0,
  )
  const guide = useDismissableBanner('posting-goal-slots-tour')
  const channel = useSelectedChannel()
  const navigate = useNavigate()
  const { account } = useAccount()
  const { config } = useCommunityConfig()

  // Channel created in the last 30 minutes
  const isRecentlyConnectedChannel =
    new Date(channel.createdAt).getTime() >= Date.now() - THIRTY_MINUTES_IN_MS

  // Check if user is on team plan
  const isOnTeamPlan = account ? isTeamPlan(account) : false

  // Check if channel is supported in Community
  const isChannelSupportedInCommunity = config.isChannelSupported(channel)

  const singleChannelCalendarMatch = useMatch(newCalendarSingleChannel.route)
  const singleChannelMatch = useMatch(channelRoute.route)

  const isInSingleChannelView =
    !!singleChannelCalendarMatch || !!singleChannelMatch

  // Filter steps based on team plan - only show team permissions step for team plan users
  const baseSteps =
    (channel.postingGoal?.goal ?? 0) > 0
      ? postingGoalSteps
      : postingScheduleSteps

  let steps = baseSteps

  // Filter out team permissions step if not on team plan
  if (!isOnTeamPlan) {
    steps = steps.filter(
      (step) => step.target !== '[data-tour-id="selected-channel-item"]',
    )
  }

  // Filter out Community step if channel is not supported in Community
  if (!isChannelSupportedInCommunity) {
    steps = steps.filter(
      (step) => step.target !== '[data-tour-id="nav-item-community"]',
    )
  }

  const { isOpen: isPostComposerOpen, createNewPostInComposer } =
    usePostComposer()

  const [run, setRun] = React.useState(false)
  const isDialogOpen = useIsDialogOpen()
  const [stepIndex, setStepIndex] = React.useState(
    parseInt(slotsTourCurrentStep, 10),
  )

  useEffect(() => {
    const currentStep = parseInt(slotsTourCurrentStep, 10)
    if (currentStep >= 0) {
      setRun(true)
    }
  }, [slotsTourCurrentStep])

  const resetTour = (): void => {
    setSlotsTourCurrentStep(TourStorageStep.CLOSED)
    setRun(false)
    setStepIndex(-1)
    guide.dismiss()
  }

  const joyrideCallback = (data: EventData): void => {
    const { action, index, status, type, size, step } = data

    if (
      action === ACTIONS.CLOSE ||
      status === STATUS.FINISHED ||
      status === STATUS.SKIPPED
    ) {
      resetTour()
      // If the user clicked action button in the last step, and has custom action, open the composer
      if (step.data?.actions?.[0]?.action === CUSTOM_ACTIONS.OPEN_COMPOSER) {
        createNewPostInComposer({
          channels: [channel.id],
        })
      }

      if (
        step.data?.actions?.[1]?.action ===
        CUSTOM_ACTIONS.NAVIGATE_TO_TEAM_SETTINGS
      ) {
        navigate('/settings/team#/team-members')
      }
      return
    }

    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const isLastStep = index === size - 1

      if (isLastStep && action === ACTIONS.NEXT) {
        // This is the last step and the user clicked "Next"
        resetTour()
      } else {
        const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1)
        setStepIndex(nextStepIndex)
        const nextStep = nextStepIndex.toString()
        if (isTourStorageStep(nextStep)) {
          setSlotsTourCurrentStep(nextStep)
        }
      }
    }
  }

  if (
    !isInSingleChannelView ||
    !guide.isActive ||
    !isRecentlyConnectedChannel ||
    isPostComposerOpen ||
    isDialogOpen
  ) {
    return <></>
  }

  return (
    <Joyride
      steps={steps}
      continuous
      run={run}
      stepIndex={stepIndex}
      onEvent={joyrideCallback}
      tooltipComponent={(props): JSX.Element => (
        <StepCoachmark {...props} open onDismiss={resetTour} />
      )}
      options={{
        hideOverlay: true,
        overlayClickAction: false,
        skipScroll: true,
        arrowColor: 'transparent',
        overlayColor: 'transparent',
      }}
      styles={{
        tooltip: {
          padding: 0,
        },
      }}
    />
  )
}
